import { Head, Link } from "@inertiajs/react"

import { HpiDataTable } from "@/components/hpi"
import type { HpiColumn } from "@/components/hpi"
import AppLayout from "@/layouts/app-layout"
import { formatDate as formatDateUtil } from "@/lib/date-utils"
import { teamPayrollPath } from "@/routes"
import type { BreadcrumbItem } from "@/types"

/* ── Types ──────────────────────────────────────────────── */

interface TimeEntryRow {
  id: number
  date: string | null
  case_id: number
  case_name: string | null
  otj_minutes: number
  travel_minutes: number
  total_minutes: number
}

interface MileageRow {
  id: number
  date: string | null
  case_id: number
  case_name: string | null
  mileage_otj: number
  mileage_travel: number
  mileage_total: number
}

interface ExpenseRow {
  id: number
  date: string | null
  case_id: number
  description: string
  amount: number
}

interface PtoRow {
  id: number
  date: string | null
  description: string
  hours: number
}

interface Summary {
  otj_minutes: number
  travel_minutes: number
  regular_minutes: number
  overtime_minutes: number
  pto_hours: number
  sto_hours: number
}

interface Props {
  employee: { id: number; name: string }
  period_start: string
  period_end: string
  summary: Summary
  time_entries: TimeEntryRow[]
  mileage_entries: MileageRow[]
  expenses: ExpenseRow[]
  pto_entries: PtoRow[]
  time_total: {
    otj_minutes: number
    travel_minutes: number
    total_minutes: number
  }
  mileage_total: {
    otj_miles: number
    travel_miles: number
    total_miles: number
  }
  expense_total: number
  return_params: { start_date?: string; end_date?: string }
}

/* ── Helpers ────────────────────────────────────────────── */

function formatMinutes(totalMin: number): string {
  if (totalMin <= 0) return ""
  const hours = Math.floor(totalMin / 60)
  const mins = totalMin % 60
  return `${hours}:${mins.toString().padStart(2, "0")}`
}

function formatDate(dateStr: string | null): string {
  return formatDateUtil(dateStr, {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  })
}

function formatPtoDays(ptoHours: number): string {
  if (ptoHours <= 0) return ""
  return String(Math.floor(ptoHours / 8))
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount)
}

/* ── Column Definitions ─────────────────────────────────── */

const summaryColumns: HpiColumn<Summary>[] = [
  {
    header: "OTJ",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.otj_minutes)}</span>
    ),
  },
  {
    header: "Travel",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.travel_minutes)}</span>
    ),
  },
  {
    header: "Reg",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.regular_minutes)}</span>
    ),
  },
  {
    header: "OT",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.overtime_minutes)}</span>
    ),
  },
  {
    header: "Hours",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium">
        {formatMinutes(row.otj_minutes + row.travel_minutes)}
      </span>
    ),
  },
  {
    header: "PTO days",
    width: "w-[80px] !flex-none",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatPtoDays(row.pto_hours)}</span>
    ),
  },
  {
    header: "STO hours",
    width: "w-[80px] !flex-none",
    align: "right",
    render: (row) => (
      <span className="text-sm">{row.sto_hours > 0 ? row.sto_hours : ""}</span>
    ),
  },
]

const timeColumns: HpiColumn<TimeEntryRow>[] = [
  {
    header: "Date",
    width: "w-[100px] !flex-none",
    render: (row) => <span className="text-sm">{formatDate(row.date)}</span>,
  },
  {
    header: "Case",
    render: (row) => <span className="text-sm">{row.case_name ?? ""}</span>,
  },
  {
    header: "OTJ",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.otj_minutes)}</span>
    ),
  },
  {
    header: "Travel",
    align: "right",
    render: (row) => (
      <span className="text-sm">{formatMinutes(row.travel_minutes)}</span>
    ),
  },
  {
    header: "Total",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium">
        {formatMinutes(row.total_minutes)}
      </span>
    ),
  },
]

const mileageColumns: HpiColumn<MileageRow>[] = [
  {
    header: "Date",
    width: "w-[100px] !flex-none",
    render: (row) => <span className="text-sm">{formatDate(row.date)}</span>,
  },
  {
    header: "Case",
    render: (row) => <span className="text-sm">{row.case_name ?? ""}</span>,
  },
  {
    header: "Miles OTJ",
    align: "right",
    render: (row) => (
      <span className="text-sm">
        {row.mileage_otj > 0 ? row.mileage_otj : ""}
      </span>
    ),
  },
  {
    header: "Miles To/From",
    align: "right",
    render: (row) => (
      <span className="text-sm">
        {row.mileage_travel > 0 ? row.mileage_travel : ""}
      </span>
    ),
  },
  {
    header: "Total",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium">{row.mileage_total}</span>
    ),
  },
]

const expenseColumns: HpiColumn<ExpenseRow>[] = [
  {
    header: "Date",
    width: "w-[100px] !flex-none",
    render: (row) => <span className="text-sm">{formatDate(row.date)}</span>,
  },
  {
    header: "Description",
    render: (row) => <span className="text-sm">{row.description}</span>,
  },
  {
    header: "Amount",
    width: "w-[120px] !flex-none",
    align: "right",
    render: (row) => (
      <span className="text-sm font-medium">{formatCurrency(row.amount)}</span>
    ),
  },
]

const ptoColumns: HpiColumn<PtoRow>[] = [
  {
    header: "Date",
    width: "w-[100px] !flex-none",
    render: (row) => <span className="text-sm">{formatDate(row.date)}</span>,
  },
  {
    header: "Description",
    render: (row) => <span className="text-sm">{row.description}</span>,
  },
  {
    header: "Hours",
    width: "w-[80px] !flex-none",
    align: "right",
    render: (row) => <span className="text-sm">{row.hours}</span>,
  },
]

/* ── Component ──────────────────────────────────────────── */

export default function PayrollDetail({
  period_start,
  period_end,
  summary,
  time_entries,
  mileage_entries,
  expenses,
  pto_entries,
  time_total,
  mileage_total,
  expense_total,
  return_params,
}: Props) {
  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Team", href: "/team" },
    { title: "Payroll History", href: teamPayrollPath(return_params) },
    {
      title: `Pay Period: ${formatDate(period_start)} to ${formatDate(period_end)}`,
    },
  ]

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head
        title={`Pay Period: ${formatDate(period_start)} to ${formatDate(period_end)}`}
      />

      <div className="p-6">
        {/* Summary section */}
        <HpiDataTable<Summary>
          title={`Pay Period: ${formatDate(period_start)} to ${formatDate(period_end)}`}
          columns={summaryColumns}
          data={[summary]}
          keyFn={() => "summary"}
        />

        {/* Time entries */}
        {time_entries.length > 0 && (
          <div className="mt-6">
            <HpiDataTable<TimeEntryRow>
              title="Time"
              columns={timeColumns}
              data={time_entries}
              keyFn={(row) => `t-${row.id}`}
              pagination={
                <div className="flex items-start gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">
                  <div className="w-[100px] flex-none" />
                  <div className="min-w-0 flex-1">Total</div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(time_total.otj_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(time_total.travel_minutes)}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {formatMinutes(time_total.total_minutes)}
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* Mileage entries */}
        {mileage_entries.length > 0 && (
          <div className="mt-6">
            <HpiDataTable<MileageRow>
              title="Mileage"
              columns={mileageColumns}
              data={mileage_entries}
              keyFn={(row) => `m-${row.id}`}
              pagination={
                <div className="flex items-start gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">
                  <div className="w-[100px] flex-none" />
                  <div className="min-w-0 flex-1">Total</div>
                  <div className="min-w-0 flex-1 text-right">
                    {mileage_total.otj_miles > 0 ? mileage_total.otj_miles : ""}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {mileage_total.travel_miles > 0
                      ? mileage_total.travel_miles
                      : ""}
                  </div>
                  <div className="min-w-0 flex-1 text-right">
                    {mileage_total.total_miles}
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* Expenses */}
        {expenses.length > 0 && (
          <div className="mt-6">
            <HpiDataTable<ExpenseRow>
              title="Expenses"
              columns={expenseColumns}
              data={expenses}
              keyFn={(row) => `e-${row.id}`}
              pagination={
                <div className="flex items-start gap-3 border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold">
                  <div className="w-[100px] flex-none" />
                  <div className="min-w-0 flex-1">Total</div>
                  <div className="w-[120px] flex-none text-right">
                    {formatCurrency(expense_total)}
                  </div>
                </div>
              }
            />
          </div>
        )}

        {/* Time Off */}
        {pto_entries.length > 0 && (
          <div className="mt-6">
            <HpiDataTable<PtoRow>
              title="Time Off"
              columns={ptoColumns}
              data={pto_entries}
              keyFn={(row) => `pto-${row.id}`}
            />
          </div>
        )}

        {/* Return link */}
        <div className="mt-6 text-center">
          <Link
            href={teamPayrollPath(return_params)}
            className="text-sm text-blue-600 hover:underline"
          >
            Return to Payroll History
          </Link>
        </div>
      </div>
    </AppLayout>
  )
}
